import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PdfLoader,
  PdfHighlighter,
  Highlight,
  AreaHighlight,
  Popup,
} from 'react-pdf-highlighter';
import 'react-pdf-highlighter/dist/style.css';
import ExternalExpertScoringPage from './ExternalExpertScoringPage';

const API_BASE = 'https://expertise.gov.kz';

function getToken() { return localStorage.getItem('token') || ''; }

/* ─── highlight colors (same as ErrorReviewPage) ─── */
const HIGHLIGHT_COLORS = [
  { id: 'yellow', bg: 'rgba(253,224,71,0.35)',  border: '#ca8a04', pin: '#ca8a04' },
  { id: 'green',  bg: 'rgba(74,222,128,0.30)',  border: '#16a34a', pin: '#16a34a' },
  { id: 'blue',   bg: 'rgba(96,165,250,0.30)',  border: '#2563eb', pin: '#2563eb' },
  { id: 'pink',   bg: 'rgba(244,114,182,0.28)', border: '#db2777', pin: '#db2777' },
  { id: 'orange', bg: 'rgba(251,146,60,0.30)',  border: '#ea580c', pin: '#ea580c' },
];
const DEFAULT_COLOR = HIGHLIGHT_COLORS[0];
function getColor(colorId) {
  return HIGHLIGHT_COLORS.find((c) => c.id === colorId) || DEFAULT_COLOR;
}
function safeJsonParse(v, fallback = null) {
  if (!v) return fallback;
  try { return typeof v === 'string' ? JSON.parse(v) : v; } catch { return fallback; }
}

/* ─── translations ─── */
function useT(language) {
  return useMemo(() => ({
    kz: {
      docType: 'Бөлім:',
      back: 'Артқа',
      loadingPdf: 'Кітап жүктелуде...',
      noPdf: 'Кітап табылмады.',
      linkNote: 'Бұл бөлім сыртқы сілтеме арқылы беріледі',
      goToLink: 'Сілтемеге өту →',
      noPdfOrLink: 'Кітап немесе сілтеме табылмады.',
      page: 'бет',
      scoring: 'Бағалау парағы',
      scoringDesc: 'Критерийлер бойынша балл қойыңыз',
      errors: 'Ескертулер',
      errorsDesc: (n) => `Барлығы: ${n}`,
      noErrors: 'Ескертулер жоқ.',
      jumpToPage: '→ өту',
      noHighlight: 'белгісіз',
      scanBadge: 'СКАН',
      downloadArchive: 'Архивті жүктеу (.zip)',
    },
    ru: {
      docType: 'Раздел:',
      back: 'Назад',
      loadingPdf: 'Документ загружается...',
      noPdf: 'Документ не найден.',
      linkNote: 'Этот раздел доступен по внешней ссылке',
      goToLink: 'Перейти по ссылке →',
      noPdfOrLink: 'Документ или ссылка не найдены.',
      page: 'стр.',
      scoring: 'Оценочный лист',
      scoringDesc: 'Выставьте баллы по критериям',
      errors: 'Замечания',
      errorsDesc: (n) => `Всего: ${n}`,
      noErrors: 'Замечаний пока нет.',
      jumpToPage: '→ перейти',
      noHighlight: 'без выделения',
      scanBadge: 'СКАН',
      downloadArchive: 'Скачать архив (.zip)',
    },
  }[language]), [language]);
}

const SIDEBAR = { NONE: 'none', SCORING: 'scoring', ERRORS: 'errors' };

/* ─── ErrorCard ─── */
function ErrorCard({ er, t, isActive, screenshotSrc, onJump }) {
  const cm = getColor(er?.highlight_color);
  const hasPos = !!(er.quads && (() => {
    try { const p = JSON.parse(er.quads); return p?.pageNumber; } catch { return false; }
  })());

  return (
    <button
      onClick={() => hasPos && onJump(er)}
      style={{
        textAlign: 'left', width: '100%',
        border: isActive ? `1.5px solid ${cm.border}` : '1px solid rgba(15,23,42,0.09)',
        background: isActive ? cm.bg : '#fff',
        borderRadius: 14, padding: '11px 13px',
        cursor: hasPos ? 'pointer' : 'default',
        boxShadow: isActive ? `0 0 0 3px ${cm.border}22` : '0 1px 4px rgba(15,23,42,0.05)',
        transition: 'all .18s ease', fontFamily: 'inherit',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 5 }}>
        <div style={{ fontWeight: 700, fontSize: 12.5, color: cm.pin, display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 9, height: 9, borderRadius: '50%', background: cm.bg, border: `1.5px solid ${cm.border}`, display: 'inline-block', flexShrink: 0 }} />
          {t.page} {er.page}
        </div>
        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          {er.screenshot_url && (
            <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: '#dc2626', borderRadius: 999, padding: '2px 7px' }}>{t.scanBadge}</span>
          )}
          <span style={{ fontSize: 11, fontWeight: 600, color: hasPos ? '#2563eb' : '#94a3b8', background: hasPos ? 'rgba(37,99,235,0.08)' : 'rgba(15,23,42,0.05)', borderRadius: 999, padding: '2px 7px' }}>
            {hasPos ? t.jumpToPage : t.noHighlight}
          </span>
        </div>
      </div>
      {er.screenshot_url && screenshotSrc && (
        <img src={screenshotSrc} alt="scan" style={{ marginTop: 6, width: '100%', borderRadius: 9, border: '1px solid rgba(15,23,42,0.08)', display: 'block' }} />
      )}
      <div style={{ marginTop: 5, fontSize: 12.5, color: '#334155', lineHeight: 1.4 }}>{er.comment}</div>
    </button>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════ */
export default function ScoringEditPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const language = localStorage.getItem('locale') || 'kz';
  const t = useT(language);

  /* ─── state ─── */
  const [activeSidebar, setActiveSidebar] = useState(SIDEBAR.NONE);
  const [pdfUrl, setPdfUrl]               = useState(null);
  const pdfUrlRef                         = useRef(null);
  const [pdfKey, setPdfKey]               = useState(0);
  const [selectedPart, setSelectedPart]   = useState('');
  const [loading, setLoading]             = useState(false);
  const [loadingPdf, setLoadingPdf]       = useState(false);
  const [parts, setParts]                 = useState([]);
  const [errors, setErrors]               = useState([]);
  const [evaluationData, setEvaluationData] = useState(null);
  const [screenshotBlobMap, setScreenshotBlobMap] = useState({});
  const [activeErrorId, setActiveErrorId] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(550);
const [resizing, setResizing]         = useState(false);
const isResizing                      = useRef(false);
const [errorsLoaded, setErrorsLoaded]   = useState(false);
const [downloadingArchive, setDownloadingArchive] = useState(false); 
const scrollToRef                       = useRef(null);
  const pdfAbortRef                       = useRef(null);

  const handleBack = () => navigate(-1);

  const handleResizeStart = (e) => {
  e.preventDefault();
  isResizing.current = true;
  setResizing(true);
  const startX = e.clientX;
  const startW = sidebarWidth;
  const onMove = (ev) => {
    if (!isResizing.current) return;
    const delta = startX - ev.clientX;
    setSidebarWidth(Math.min(700, Math.max(300, startW + delta)));
  };
  const onUp = () => {
    isResizing.current = false;
    setResizing(false);
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
  };
  window.addEventListener('mousemove', onMove);
  window.addEventListener('mouseup', onUp);
};

const handleDownloadArchive = useCallback(async (url, name) => {  // ← добавить
  if (!url || downloadingArchive) return;
  try {
    setDownloadingArchive(true);
    const resp = await axios.get(url, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(new Blob([resp.data]));
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = name || 'archive.zip';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch {
    alert(language === 'ru' ? '❌ Не удалось скачать архив.' : '❌ Архивті жүктеу мүмкін болмады.');
  } finally {
    setDownloadingArchive(false);
  }
}, [downloadingArchive, language]);
  const toggleSidebar = (type) => setActiveSidebar((prev) => prev === type ? SIDEBAR.NONE : type);

  

  /* ─── load parts ─── */
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    (async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/api/expertise/external/applications/${id}/evaluation`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        setEvaluationData(res.data);
        const files = res.data.files || [];
        const partsData = files.map((file) => ({
  value: String(file.id),
  label: file.application_file_name || `Бөлім ${file.section}`,
  id: String(file.id),
  pdfUrl: file.file_path ? `${API_BASE}/api/expertise/internal-files/${file.id}/pdf` : null,
  file_link: file.file_link ?? null,
  archiveUrl: file.section_archive_download_url ?? null,   
  archiveName: file.section_archive_name ?? null,         
}));
        setParts(partsData);
        if (partsData.length > 0) setSelectedPart(partsData[0].value);
      } catch (err) { }
      finally { setLoading(false); }
    })();
  }, [id]);

  /* ─── load errors ─── */
  useEffect(() => {
    if (!id) return;
    setErrorsLoaded(false);
    axios.get(`${API_BASE}/api/expertise/book-errors?book=${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => {
        let savedColors = {};
        try { savedColors = JSON.parse(localStorage.getItem(`hl_colors_${id}`) || '{}'); } catch {}
        const loaded = (res.data?.results || []).map((er) => ({
          ...er,
          highlight_color: er.highlight_color || savedColors[String(er.id)] || DEFAULT_COLOR.id,
        }));
        setErrors(loaded);
      })
      .catch(() => setErrors([]))
      .finally(() => setErrorsLoaded(true));
  }, [id]);

  /* ─── load screenshots ─── */
  useEffect(() => {
    const withScreens = errors.filter((er) => !!er?.screenshot_url);
    if (!withScreens.length) { setScreenshotBlobMap({}); return; }
    let cancelled = false;
    const created = [];
    (async () => {
      const nextMap = {};
      await Promise.all(withScreens.map(async (er) => {
        try {
          const res = await axios.get(er.screenshot_url, {
            responseType: 'blob',
            headers: { Authorization: `Bearer ${getToken()}` },
          });
          const u = URL.createObjectURL(res.data);
          created.push(u);
          nextMap[String(er.id)] = u;
        } catch {}
      }));
      if (!cancelled) setScreenshotBlobMap(nextMap);
    })();
    return () => {
      cancelled = true;
      for (const u of created) { try { URL.revokeObjectURL(u); } catch {} }
    };
  }, [errors]);

  /* ─── load PDF ─── */
  useEffect(() => {
    const part = parts.find((p) => p.value === selectedPart);
    if (!part || part.file_link) {
      setPdfUrl((prev) => { try { if (prev) URL.revokeObjectURL(prev); } catch {} pdfUrlRef.current = null; return null; });
      return;
    }
    if (!part.pdfUrl) {
      setPdfUrl((prev) => { try { if (prev) URL.revokeObjectURL(prev); } catch {} pdfUrlRef.current = null; return null; });
      return;
    }
    if (pdfAbortRef.current) pdfAbortRef.current.abort();
    const controller = new AbortController();
    pdfAbortRef.current = controller;
    setLoadingPdf(true);
    axios.get(part.pdfUrl, {
      responseType: 'blob',
      headers: { Authorization: `Bearer ${getToken()}` },
      signal: controller.signal,
    })
      .then((res) => {
        const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        setPdfUrl((prev) => { try { if (prev) URL.revokeObjectURL(prev); } catch {} pdfUrlRef.current = blobUrl; return blobUrl; });
        setPdfKey((k) => k + 1);
      })
      .catch((e) => {
        if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
        setPdfUrl((prev) => { try { if (prev) URL.revokeObjectURL(prev); } catch {} pdfUrlRef.current = null; return null; });
      })
      .finally(() => setLoadingPdf(false));

    return () => { controller.abort(); };
  }, [selectedPart, parts]);

  /* ─── cleanup ─── */
  useEffect(() => {
    return () => {
      if (pdfAbortRef.current) pdfAbortRef.current.abort();
      try { if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current); } catch {}
    };
  }, []);

  /* ─── filtered errors for current part ─── */
  const filteredErrors = useMemo(() => {
    const partId = parts.find((p) => p.value === selectedPart)?.id;
    if (!partId) return [];
    return errors.filter((er) => String(er.file_id) === partId || String(er.file) === partId);
  }, [errors, parts, selectedPart]);

  const currentPart = parts.find((p) => p.value === selectedPart);

  /* ─── highlights for PdfHighlighter ─── */
  const highlights = useMemo(() =>
    filteredErrors.map((er) => {
      const pos = safeJsonParse(er.quads, null);
      if (!pos || !pos.pageNumber) return null;
      return {
        id: String(er.id),
        position: pos,
        content: { text: '', image: er.screenshot_url ? 'scan' : null },
        comment: { text: er.comment || '' },
        __raw: er,
      };
    }).filter(Boolean),
  [filteredErrors]);

  const highlightById = useMemo(() => {
    const m = new Map();
    for (const h of highlights) m.set(String(h.__raw?.id), h);
    return m;
  }, [highlights]);

  /* ─── jump to highlight ─── */
  const handleJumpToError = useCallback((er) => {
    const h = highlightById.get(String(er.id));
    setActiveErrorId(String(er.id));
    if (h && scrollToRef.current) {
      scrollToRef.current(h);
    }
  }, [highlightById]);

  /* ─── highlight CSS (color per error) ─── */
  const highlightCss = useMemo(() => {
    const reset = `
      .ScoringPdfViewer .Highlight__part { background: transparent !important; border-bottom: none !important; }
      .ScoringPdfViewer .AreaHighlight__part { background: transparent !important; border: none !important; }
    `;
    const perItem = filteredErrors.map((er) => {
      const cm = getColor(er?.highlight_color);
      return `
        [data-hl-id="${er.id}"] .Highlight__part {
          background: ${cm.bg} !important;
          border-bottom: 2px solid ${cm.border} !important;
          border-radius: 2px !important;
        }
        [data-hl-id="${er.id}"] .AreaHighlight__part {
          background: ${cm.bg} !important;
          border: 2px solid ${cm.border} !important;
          border-radius: 4px !important;
        }
      `;
    }).join('');
    return reset + perItem;
  }, [filteredErrors]);

  const sidebarOpen = activeSidebar !== SIDEBAR.NONE;

  /* ═══════════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════════ */
  return (
    <div className="ScoringPdfViewer" style={{
      position: 'relative', display: 'flex', flexDirection: 'column',
      height: '100vh', width: '100vw', overflow: 'hidden',
      fontFamily: "'Segoe UI', system-ui, sans-serif",
      background: '#f1f4f9',
    }}>
      <style>{`
        .ScoringPdfViewer .PdfHighlighter,
        .ScoringPdfViewer .PdfHighlighter__viewer {
          width: 100% !important; min-width: 0 !important; height: 100% !important;
        }
        .ScoringPdfViewer .PdfHighlighter__viewer {
          position: relative !important; overflow-y: auto !important; overflow-x: auto !important;
          background: #dde1e9;
          scrollbar-width: thin; scrollbar-color: #cbd5e1 transparent;
        }
        .ScoringPdfViewer .PdfHighlighter__viewer::-webkit-scrollbar { width: 5px; }
        .ScoringPdfViewer .PdfHighlighter__viewer::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 99px; }
        .ScoringPdfViewer .PdfHighlighter__tip-container { z-index: 9999 !important; }
        .ScoringPdfViewer .PdfHighlighter__viewer .pdfViewer { max-width: 100% !important; }
        .ScoringPdfViewer .PdfHighlighter__page,
        .ScoringPdfViewer .PdfHighlighter__viewer .page { margin: 0 auto 12px !important; }
        @keyframes sc-spin { to { transform: rotate(360deg); } }
        @keyframes scanPulse {
          0%   { box-shadow: 0 0 0 0 rgba(220,38,38,.35); }
          70%  { box-shadow: 0 0 0 10px rgba(220,38,38,0); }
          100% { box-shadow: 0 0 0 0 rgba(220,38,38,0); }
        }
      `}</style>
      <style>{highlightCss}</style>

      {/* ══ TOPBAR ══ */}
      <div style={{
        height: 56, borderBottom: '1px solid rgba(15,23,42,0.08)',
        background: '#fff', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', padding: '0 16px', gap: 12,
        flexShrink: 0, boxShadow: '0 1px 4px rgba(15,23,42,0.06)', zIndex: 30,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={handleBack} style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            padding: '6px 12px', borderRadius: 8,
            border: '1px solid rgba(15,23,42,0.12)', background: '#f8fafc',
            cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#334155',
          }}>
            <span style={{ fontSize: 15 }}>←</span>{t.back}
          </button>
          <div style={{ width: 1, height: 24, background: 'rgba(15,23,42,0.1)' }} />
          <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600 }}>{t.docType}</span>
          <select
            value={selectedPart}
            onChange={(e) => setSelectedPart(e.target.value)}
            style={{
              padding: '5px 24px 5px 9px', fontSize: 13, borderRadius: 8,
              border: '1px solid rgba(15,23,42,0.12)', background: '#f8fafc',
              color: '#0f172a', cursor: 'pointer', outline: 'none',
              appearance: 'none', fontFamily: 'inherit',
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath fill='%2394a3b8' d='M5 7L1 3h8z'/%3E%3C/svg%3E\")",
              backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center',
            }}
          >
            {parts.map((part) => (
              <option key={part.value} value={part.value}>{part.label}</option>
            ))}
          </select>

          {/* ── Archive download ── */}
          {(() => {
            const cur = parts.find((p) => p.value === selectedPart);
            if (!cur?.archiveUrl) return null;
            return (
              <button
                onClick={() => handleDownloadArchive(cur.archiveUrl, cur.archiveName)}
                disabled={downloadingArchive}
                title={cur.archiveName || 'Архив'}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '5px 11px', borderRadius: 8,
                  border: '1px solid rgba(8,145,178,0.35)',
                  background: downloadingArchive ? '#f8fafc' : 'rgba(8,145,178,0.06)',
                  color: '#0891b2', cursor: downloadingArchive ? 'default' : 'pointer',
                  fontSize: 12, fontWeight: 700, transition: 'all .15s',
                  opacity: downloadingArchive ? 0.6 : 1,
                }}
              >
                {downloadingArchive ? (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'sc-spin 0.9s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                  </svg>
                ) : (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                )}
                {language === 'ru' ? 'Архив' : 'Архив'}
              </button>
            );
          })()}
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => toggleSidebar(SIDEBAR.SCORING)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '7px 14px', borderRadius: 10,
              border: activeSidebar === SIDEBAR.SCORING ? '1.5px solid #2563eb' : '1px solid rgba(15,23,42,0.12)',
              background: activeSidebar === SIDEBAR.SCORING ? '#eff6ff' : '#fff',
              cursor: 'pointer', fontSize: 13, fontWeight: 700,
              color: activeSidebar === SIDEBAR.SCORING ? '#2563eb' : '#334155',
              transition: 'all .15s ease',
            }}
          >
            <ScoreIcon active={activeSidebar === SIDEBAR.SCORING} />
            {t.scoring}
          </button>

          <button
            onClick={() => toggleSidebar(SIDEBAR.ERRORS)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '7px 14px', borderRadius: 10,
              border: activeSidebar === SIDEBAR.ERRORS ? '1.5px solid #dc2626' : '1px solid rgba(15,23,42,0.12)',
              background: activeSidebar === SIDEBAR.ERRORS ? '#fef2f2' : '#fff',
              cursor: 'pointer', fontSize: 13, fontWeight: 700,
              color: activeSidebar === SIDEBAR.ERRORS ? '#dc2626' : '#334155',
              transition: 'all .15s ease', position: 'relative',
            }}
          >
            <ErrorIcon active={activeSidebar === SIDEBAR.ERRORS} />
            {t.errors}
            {errorsLoaded && filteredErrors.length > 0 && (
              <span style={{
                background: activeSidebar === SIDEBAR.ERRORS ? '#dc2626' : '#ef4444',
                color: '#fff', borderRadius: 999,
                fontSize: 10, fontWeight: 800, padding: '1px 6px', minWidth: 18, textAlign: 'center',
              }}>
                {filteredErrors.length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* ══ MAIN ══ */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>

        {/* PDF with PdfHighlighter */}
        <div style={{
          position: 'absolute', inset: 0,
          marginRight: sidebarOpen ? sidebarWidth : 0,
          transition: resizing ? 'none' : 'margin-right 0.32s cubic-bezier(.4,0,.2,1)',
          background: '#dde1e9', display: 'flex', flexDirection: 'column',
        }}>
          {loading ? (
            <CenterSpinner text={t.loadingPdf} />
          ) : !currentPart ? (
            <CenterText text={t.noPdf} />
          ) : currentPart.file_link && !pdfUrl ? (
  <LinkState
    t={t}
    link={currentPart.file_link}
    archiveUrl={currentPart.archiveUrl}
    archiveName={currentPart.archiveName}
    downloadingArchive={downloadingArchive}
    onDownloadArchive={handleDownloadArchive}
  />
          ) : loadingPdf ? (
            <CenterSpinner text={t.loadingPdf} />
          ) : !pdfUrl ? (
            <CenterText text={t.noPdfOrLink} />
          ) : (
            <PdfLoader
              key={pdfKey}
              url={pdfUrl}
              beforeLoad={<CenterSpinner text={language === 'ru' ? 'Открываю PDF…' : 'PDF ашылуда…'} />}
            >
              {(pdfDocument) => (
                <PdfHighlighter
                  key={`hl-${pdfKey}`}
                  pdfDocument={pdfDocument}
                  pdfScaleValue="page-width"
                  enableAreaSelection={() => false}
                  onScrollChange={() => {}}
                  scrollRef={(scrollTo) => { scrollToRef.current = scrollTo; }}
                  onSelectionFinished={() => null}
                  highlightTransform={(highlight, _index, setTip, hideTip, _v, _s, isScrolledTo) => {
                    const er = highlight.__raw;
                    const isActive = String(er?.id) === String(activeErrorId);
                    const isScan = !!er?.screenshot_url;
                    const screenshotSrc = screenshotBlobMap[String(er?.id)] || null;
                    const cm = getColor(er?.highlight_color);

                    const component = isScan ? (
                      <AreaHighlight isScrolledTo={isScrolledTo} highlight={highlight} onChange={() => {}} />
                    ) : (
                      <Highlight isScrolledTo={isScrolledTo} position={highlight.position} comment={highlight.comment} />
                    );

                    const pin = (
                      <div style={{
                        position: 'absolute', left: 6, top: 6,
                        width: 22, height: 22, borderRadius: 999,
                        background: isScan ? '#dc2626' : cm.pin,
                        color: '#fff', fontSize: 12, fontWeight: 900,
                        display: 'grid', placeItems: 'center',
                        boxShadow: isActive ? `0 0 0 4px ${cm.bg}, 0 6px 16px ${cm.pin}66` : `0 3px 10px ${cm.pin}55`,
                        pointerEvents: 'none',
                        animation: isScan ? 'scanPulse 1.5s infinite' : 'none',
                      }}>!</div>
                    );

                    return (
                      <Popup
                        key={highlight.id}
                        popupContent={
                          <div style={{
                            maxWidth: 260, background: '#fff',
                            border: '1px solid rgba(15,23,42,0.09)', borderRadius: 14,
                            padding: '11px 13px', boxShadow: '0 12px 36px rgba(15,23,42,0.13)',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5 }}>
                              <span style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                                {language === 'ru' ? 'Стр.' : 'Бет'} {er?.page}
                              </span>
                              <span style={{ width: 9, height: 9, borderRadius: '50%', background: cm.bg, border: `1.5px solid ${cm.border}`, display: 'inline-block', flexShrink: 0 }} />
                            </div>
                            <div style={{ fontSize: 12.5, color: '#374151', lineHeight: 1.4 }}>{er?.comment}</div>
                            {isScan && screenshotSrc && (
                              <img src={screenshotSrc} alt="scan" style={{ marginTop: 8, width: '100%', borderRadius: 8, border: '1px solid rgba(15,23,42,0.08)', display: 'block' }} />
                            )}
                          </div>
                        }
                        onMouseOver={(popupContent) => setTip(highlight, () => popupContent)}
                        onMouseOut={hideTip}
                      >
                        <div data-hl-id={highlight.id} style={{ position: 'relative', overflow: 'visible' }}>
                          {component}
                          {pin}
                          {isActive && (
                            <div style={{
                              position: 'absolute', inset: -2, borderRadius: 8,
                              outline: `2px solid ${cm.border}`,
                              outlineOffset: 1, pointerEvents: 'none',
                            }} />
                          )}
                        </div>
                      </Popup>
                    );
                  }}
                  highlights={highlights}
                />
              )}
            </PdfLoader>
          )}
        </div>

        {/* ══ RIGHT SIDEBAR ══ */}
        <div style={{
          position: 'absolute', top: 0, right: 0, bottom: 0, width: sidebarWidth,
          background: '#fff',
          boxShadow: sidebarOpen ? '-3px 0 16px rgba(15,23,42,0.1)' : 'none',
          transform: sidebarOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: resizing ? 'none' : 'transform 0.32s cubic-bezier(.4,0,.2,1)',
          pointerEvents: sidebarOpen ? 'auto' : 'none',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden', zIndex: 20,
          borderLeft: '1px solid rgba(15,23,42,0.08)',
        }}>

          <div
  onMouseDown={handleResizeStart}
  style={{
    position: 'absolute', left: 0, top: 0, bottom: 0, width: 12,
    cursor: 'col-resize', zIndex: 10,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: resizing ? 'rgba(37,99,235,0.15)' : 'transparent',
    transition: 'background .15s',
  }}
  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(37,99,235,0.1)'}
  onMouseLeave={(e) => { if (!resizing) e.currentTarget.style.background = 'transparent'; }}
>
  <div style={{
    width: 3, height: 32, borderRadius: 99,
    background: resizing ? '#2563eb' : 'rgba(15,23,42,0.15)',
    transition: 'background .15s, height .15s',
  }} />
</div>
          {activeSidebar === SIDEBAR.SCORING && (
            <ScoringPanel
              t={t}
              evaluationData={evaluationData}
              applicationId={id}
              onClose={() => setActiveSidebar(SIDEBAR.NONE)}
            />
          )}
          {activeSidebar === SIDEBAR.ERRORS && (
            <ErrorsPanel
              t={t}
              errors={filteredErrors}
              screenshotBlobMap={screenshotBlobMap}
              activeErrorId={activeErrorId}
              onJump={handleJumpToError}
              onClose={() => setActiveSidebar(SIDEBAR.NONE)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ── ScoringPanel ── */
function ScoringPanel({ t, evaluationData, applicationId, onClose }) {
  const saveRef = useRef(null);
  const [saving, setSaving] = useState(false);

  const handleHeaderSave = async () => {
    if (!saveRef.current || saving) return;
    setSaving(true);
    await saveRef.current();
    setSaving(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{
        padding: '10px 14px', borderBottom: '1px solid rgba(15,23,42,0.07)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        flexShrink: 0, background: '#fff', gap: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span style={{ width: 28, height: 28, borderRadius: 8, background: '#eff6ff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <ScoreIcon active={true} size={14} />
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{t.scoring}</div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>{t.scoringDesc}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <button
            onClick={handleHeaderSave}
            disabled={saving}
            style={{
              padding: '6px 12px', borderRadius: 8, border: 'none',
              background: saving ? '#e2e8f0' : '#2563eb',
              color: saving ? '#94a3b8' : '#fff',
              fontSize: 12, fontWeight: 700, cursor: saving ? 'default' : 'pointer',
              transition: 'all .15s', fontFamily: 'inherit',
            }}
          >
            {saving ? '...' : localStorage.getItem('locale') === 'ru' ? 'Сохранить' : 'Сақтау'}
          </button>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8', fontSize: 18, lineHeight: 1, padding: 4, borderRadius: 6 }}>✕</button>
        </div>
      </div>
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <ExternalExpertScoringPage applicationId={applicationId} evaluationData={evaluationData} saveRef={saveRef} />
      </div>
    </div>
  );
}

/* ── ErrorsPanel ── */
function ErrorsPanel({ t, errors, screenshotBlobMap, activeErrorId, onJump, onClose }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{
        padding: '14px 18px', borderBottom: '1px solid rgba(15,23,42,0.07)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        flexShrink: 0, background: '#fff',
      }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 28, height: 28, borderRadius: 8, background: '#fef2f2', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <ErrorIcon active={true} size={14} />
            </span>
            {t.errors}
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3, marginLeft: 36 }}>{t.errorsDesc(errors.length)}</div>
        </div>
        <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8', fontSize: 18, lineHeight: 1, padding: 4, borderRadius: 6 }}>✕</button>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {errors.length === 0 ? (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 13, fontStyle: 'italic' }}>
            {t.noErrors}
          </div>
        ) : (
          errors.map((er) => (
            <ErrorCard
              key={er.id} er={er} t={t}
              isActive={String(er.id) === String(activeErrorId)}
              screenshotSrc={screenshotBlobMap[String(er.id)] || null}
              onJump={onJump}
            />
          ))
        )}
      </div>
    </div>
  );
}

/* ── small helpers ── */
function CenterSpinner({ text }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14 }}>
      <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid rgba(37,99,235,0.15)', borderTopColor: '#2563eb', animation: 'sc-spin 0.9s linear infinite' }} />
      <p style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic', margin: 0 }}>{text}</p>
    </div>
  );
}
function CenterText({ text }) {
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>{text}</div>
  );
}
function LinkState({ t, link, archiveUrl, archiveName, downloadingArchive, onDownloadArchive }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 32 }}>
      <div style={{ fontSize: 13, color: '#64748b', textAlign: 'center' }}>{t.linkNote}</div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
  <a
    href={link}
    target="_blank"
    rel="noopener noreferrer"
    style={{
      display: 'inline-block',
      padding: '10px 22px',
      background: '#2563eb',
      color: '#fff',
      borderRadius: 10,
      textDecoration: 'none',
      fontSize: 14,
      fontWeight: 700,
    }}
  >
    {t.goToLink}
  </a>

        {archiveUrl && (
          <button
            onClick={() => onDownloadArchive(archiveUrl, archiveName)}
            disabled={downloadingArchive}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '10px 22px', borderRadius: 10,
              border: '1.5px solid #0891b2', color: '#0891b2',
              background: '#fff', cursor: downloadingArchive ? 'default' : 'pointer',
              fontWeight: 700, fontSize: 14,
              opacity: downloadingArchive ? 0.7 : 1,
              transition: 'opacity .15s ease',
            }}
          >
            {downloadingArchive ? (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                  style={{ animation: 'sc-spin 0.9s linear infinite', flexShrink: 0 }}>
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                </svg>
                Жүктелуде…
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                {t.downloadArchive}
              </>
            )}
          </button>
        )}
      </div>

      {archiveName && (
        <div style={{ fontSize: 11.5, color: '#94a3b8' }}>📦 {archiveName}</div>
      )}
    </div>
  );
}
function ScoreIcon({ active, size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke={active ? '#2563eb' : '#64748b'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12L6 8l3 3 5-6" />
      <circle cx="13" cy="3" r="1.2" fill={active ? '#2563eb' : '#64748b'} stroke="none" />
    </svg>
  );
}
function ErrorIcon({ active, size = 15 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke={active ? '#dc2626' : '#64748b'} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 2L14 13H2L8 2Z" />
      <line x1="8" y1="6" x2="8" y2="9" />
      <circle cx="8" cy="11" r="0.7" fill={active ? '#dc2626' : '#64748b'} stroke="none" />
    </svg>
  );
}